// Shared error surfaces for the Backend screens.
//
// `QueryErrorNote` fills a table's empty-state slot when a read fails (the
// screens' three-way loading/empty/no-match guard gains a fourth arm), and
// `MutationErrorLine` is the inline one-liner for failed writes in modals and
// drawers. Both surface the `ApiError` fields (HTTP status + server message +
// error code) so a failure is debuggable straight from the screen instead of
// looking like an eternal "Loading…".

import { ApiError } from '@/api/errors';

function describe(error: unknown): { headline: string; detail: string | null } {
  if (error instanceof ApiError) {
    return {
      headline: `${error.status} ${error.code}`,
      detail: error.message,
    };
  }
  if (error instanceof Error) {
    // fetch() network failures land here as `NetworkError`, which extends Error
    // and carries the original browser message ('Failed to fetch' on Chrome,
    // 'NetworkError when attempting to fetch resource' on Firefox).
    return { headline: 'Request failed', detail: error.message };
  }
  return { headline: 'Request failed', detail: null };
}

export function QueryErrorNote({
  error,
  what,
  onRetry,
}: {
  error: unknown;
  what: string;
  onRetry?: () => void;
}) {
  const { headline, detail } = describe(error);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 12 }}>
      <div className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bad)' }}>
        ⚠ Couldn’t load {what} · {headline}
      </div>
      {detail && (
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-2)', maxWidth: 520, textAlign: 'center' }}>
          {detail}
        </div>
      )}
      {onRetry && (
        <button className="btn btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function MutationErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  const { headline, detail } = describe(error);
  return (
    <div className="mono" style={{ fontSize: 11.5, color: 'var(--bad)', lineHeight: 1.4 }}>
      ● {headline}
      {detail ? ` — ${detail}` : ''}
    </div>
  );
}
